import React, { Key, useRef, useState, useEffect } from "react";
import "./browser_spareparts_sales.css";
import { Button, Input, InputRef, Space, Table, TableColumnsType, TableProps } from "antd";
import useSWR from "swr";
import { FilterConfirmProps, FilterDropdownProps, ColumnType, SorterResult, FilterValue } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { useAuth } from "../../context/AuthContext";

export interface DataType {
    key: React.Key;
    id: number;
    id_substitution: number | null;
    status: string;
    brand: string;
    model: string;
    item_code: string;
    item_code1: string | null;
    item_code2: string | null;
    item_name: string;
    qty: number;
    hpp: number;
    sales_price: number;
    uom: string;
    uom_rate: number;
    warehouse_group: string;
    remark: string | null;
}

interface ApiResponse {
    data: DataType[];
    total: number;
}

interface TableParams {
    pagination?: {
        current?: number;
        pageSize?: number;
        total?: number;
    };
    sortField?: SorterResult<DataType>['field'];
    sortOrder?: SorterResult<DataType>['order'];
    filters?: Record<string, FilterValue | null>;
}

interface BrowserSparepartsSalesProps {
    isVisible: boolean;
    onClose: () => void;
    onSelectSparePart: (item: { item_code: string; item_name: string; uom: string; sales_price: number; qty: number; }) => void;
}

const fetcher = async ([url, params, warehouseCode]: [string, TableParams, string | undefined]) => {
    const searchParams = new URLSearchParams();

    if (params.pagination?.current) {
        searchParams.append('page', params.pagination.current.toString());
    }
    if (params.pagination?.pageSize) {
        searchParams.append('pageSize', params.pagination.pageSize.toString());
    }
    if (params.sortField && params.sortOrder) {
        searchParams.append('sortBy', String(params.sortField));
        searchParams.append('order', params.sortOrder === 'ascend' ? 'asc' : 'desc');
    }
    if (warehouseCode) {
        searchParams.append('warehouse_code', warehouseCode);
    }

    if (params.filters) {
        Object.keys(params.filters).forEach(key => {
            const filterValue = params.filters?.[key];
            if (filterValue && filterValue.length > 0) {
                let paramKey = key;
                if (key === 'brand') {
                    paramKey = 'brandDescription';
                } else if (key === 'item_class') {
                    paramKey = 'itemClassDescription';
                }
                searchParams.append(key, filterValue.join(','));
            }
        });
    }

    const fullUrl = `${url}?${searchParams.toString()}`;
    const res = await fetch(fullUrl);
    if (!res.ok) {
        throw new Error(`Error fetching data: ${res.statusText}`);
    }
    return res.json();
};



const BrowserSparepartsSales: React.FC<BrowserSparepartsSalesProps> = ({ isVisible, onClose, onSelectSparePart }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const { userData } = useAuth();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10,
        },
    });

    const { data: apiResponse, error, isLoading } = useSWR<ApiResponse>(
        userData?.warehouse_code
            ? [`${process.env.REACT_APP_BACKEND_URL}/api/spareparts-sales`, tableParams, userData.warehouse_code]
            : null,
        fetcher
    );

    const data = apiResponse?.data;
    const total = apiResponse?.total;

    useEffect(() => {
        console.log("SWR Data:", data);
        console.log("SWR Error:", error);
        console.log("SWR IsLoading:", isLoading);
    }, [data, error, isLoading]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: keyof DataType,
    ) => {
        confirm();

        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);

        setTableParams(prev => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                current: 1,
            },
            filters: {
                ...prev.filters,
                [dataIndex]: selectedKeys.length ? selectedKeys : null,
            },
        }));
    };

    const handleReset = (clearFilters: () => void, dataIndex: keyof DataType) => {
        clearFilters();

        setSearchText('');
        setSearchedColumn('');

        setTableParams(prev => {
            const newFilters = { ...prev.filters };
            delete newFilters[dataIndex];
            return {
                ...prev,
                pagination: {
                    ...prev.pagination,
                    current: 1,
                },
                filters: newFilters,
            };
        });
    };

    const getColumnSearchProps = (dataIndex: keyof DataType): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: FilterDropdownProps) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: tableParams.filters?.[dataIndex]?.length ? '#1677ff' : undefined }} />
        ),
        filterDropdownProps: {
            onOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        onFilterDropdownOpenChange: (visible: boolean) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text: string) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? String(text) : ''}
                />
            ) : (
                text
            ),
    });

    const handleTableChange: TableProps<DataType>['onChange'] = (
        pagination,
        filters,
        sorter,
    ) => {
        setTableParams({
            pagination,
            filters,
            sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
            sortField: Array.isArray(sorter) ? undefined : sorter.field,
        });
    };

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            ...getColumnSearchProps('item_code'),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            sorter: true,
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            ellipsis: true,
            ...getColumnSearchProps('item_name'),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            sorter: true,
        },
        {
            title: 'Available Qty',
            dataIndex: 'qty',
            key: 'qty',
            align: 'left',
            ...getColumnSearchProps('qty'),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            sorter: true,
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            ...getColumnSearchProps('brand'),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            sorter: true,
        },
        {
            title: 'Sales Price',
            dataIndex: 'sales_price',
            key: 'sales_price',
            align: 'right',
            ...getColumnSearchProps('sales_price'),
            render: (value: any) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0',
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            sorter: true,
        },
        {
            title: 'UOM',
            dataIndex: 'uom',
            key: 'uom',
            ...getColumnSearchProps('uom'),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            sorter: true,
        },
    ];

    if (error) {
        console.error('Error fetching spareparts:', error);
        return <div>Gagal memuat data spareparts</div>;
    }

    return (

        <div id="iqfi" style={transformStyle}>
            <div className="gjs-grid-row" id="ioco">
                <div className="gjs-grid-column" id="it8s">
                    <Table
                        columns={columns}
                        dataSource={data}
                        size="small"
                        bordered
                        loading={isLoading || !userData?.warehouse_code}
                        pagination={{
                            ...tableParams.pagination,
                            total: total,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        onChange={handleTableChange}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    onSelectSparePart({
                                        item_code: record.item_code,
                                        item_name: record.item_name,
                                        uom: record.uom,
                                        sales_price: record.sales_price,
                                        qty: record.qty,
                                    });
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserSparepartsSales;